package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.LoginViewModel
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.UpdateActiveRoleRequest
import com.arena_web.data.websocket.UserWsEvent
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.launch
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

@Composable
fun MobileBusinessTopBar(
    clubs: List<ClubDto>,
    selectedClubId: String?,
    onClubSelected: (String) -> Unit,
    onAddClub: () -> Unit
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    var isLoggedIn by remember { mutableStateOf(Storage.contains(StorageKeys.ACCESS_TOKEN)) }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    val notificationCount by container.unreadNotificationCount.collectAsState()

    var showClubDropdown by remember { mutableStateOf(false) }
    var showProfileDropdown by remember { mutableStateOf(false) }

    LaunchedEffect(isLoggedIn) {
        if (!isLoggedIn) return@LaunchedEffect
        container.ensureNotificationsCountLoaded()
    }

    LaunchedEffect(isLoggedIn) {
        if (!isLoggedIn) return@LaunchedEffect
        container.userWsClient.events.collect { event ->
            if (event is UserWsEvent.NewNotification) {
                container.refreshNotifications()
            }
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(44.px)
            .styleModifier { property("flex-shrink", "0") },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left: Logo
        Box(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { ctx.router.navigateTo(Routes.DASHBOARD) },
            contentAlignment = Alignment.Center
        ) {
            com.arena_web.ui.sections.header.components.BrandLogo()
        }

        if (isLoggedIn) {
            // Center: Club Selector
            MobileClubSelector(
                clubs = clubs,
                selectedClubId = selectedClubId,
                showDropdown = showClubDropdown,
                onToggle = {
                    showClubDropdown = !showClubDropdown
                    showProfileDropdown = false
                },
                onClubSelected = { id ->
                    onClubSelected(id)
                    showClubDropdown = false
                },
                onAddClub = {
                    onAddClub()
                    showClubDropdown = false
                }
            )

            // Right: Profile avatar with notification badge
            MobileBusinessProfileButton(
                avatarUrl = avatarUrl,
                notificationCount = notificationCount,
                showDropdown = showProfileDropdown,
                onToggle = {
                    showProfileDropdown = !showProfileDropdown
                    showClubDropdown = false
                },
                onLoggedOut = {
                    isLoggedIn = false
                    avatarUrl = null
                    container.updateNotificationCount(0)
                }
            )
        } else {
            // Spacer to keep layout balanced
            Box(Modifier)

            // Right: Auth buttons
            val onLoggedIn: () -> Unit = {
                avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
                isLoggedIn = true
            }
            Row(
                modifier = Modifier.gap(6.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                MobileBusinessGoogleButton(onLoggedIn = onLoggedIn)
                MobileBusinessAppleButton(onLoggedIn = onLoggedIn)
            }
        }
    }
}

// =============================================================================
// Club Selector
// =============================================================================

@Composable
private fun MobileClubSelector(
    clubs: List<ClubDto>,
    selectedClubId: String?,
    showDropdown: Boolean,
    onToggle: () -> Unit,
    onClubSelected: (String) -> Unit,
    onAddClub: () -> Unit
) {
    val t = LocalAppStrings.current
    val selectedClub = clubs.find { it.id == selectedClubId }

    Box(modifier = Modifier.position(Position.Relative)) {
        Row(
            modifier = Modifier
                .height(44.px)
                .borderRadius(26.px)
                .padding(leftRight = 16.px, topBottom = 8.px)
                .gap(8.px)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid var(--arena-border-top)")
                    property(
                        "background",
                        AppGradients.InteractiveSecondary
                    )
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                selectedClub?.name ?: t.headerSelectClub,
                modifier = Modifier
                    .fontSize(16.px)
                    .fontWeight(500)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("max-width", "160px")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Icon(
                src = AppIcons.ChevronDown,
                modifier = Modifier.size(24.px)
            )
        }

        if (showDropdown && clubs.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(50.px)
                    .left(0.px)
                    .borderRadius(12.px)
                    .padding(6.px)
                    .zIndex(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, var(--l-s2, #0A0D14), var(--l-s2, #0A0D14)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("min-width", "180px")
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(2.px)) {
                    clubs.forEach { club ->
                        val isSelected = club.id == selectedClubId
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .borderRadius(8.px)
                                .padding(10.px, 12.px)
                                .cursor(Cursor.Pointer)
                                .then(
                                    if (isSelected) Modifier.styleModifier {
                                        property("background", "rgba(0, 97, 255, 0.12)")
                                    } else Modifier
                                )
                                .onClick { onClubSelected(club.id) },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                club.name,
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .fontWeight(500)
                                    .color(if (isSelected) org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)") else com.arena_web.ui.theme.ThemeVars.Text2)
                            )
                        }
                    }

                    // Divider
                    Box(
                        Modifier.fillMaxWidth().height(1.px).styleModifier {
                            property("background", "var(--arena-glass-strong)")
                        }
                    )

                    // Add Club
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .borderRadius(8.px)
                            .padding(10.px, 12.px)
                            .gap(8.px)
                            .cursor(Cursor.Pointer)
                            .onClick { onAddClub() },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(src = AppIcons.Add, modifier = Modifier.size(16.px))
                        SpanText(
                            t.headerAddClub,
                            modifier = Modifier
                                .fontSize(14.px)
                                .fontWeight(500)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }
                }
            }
        }
    }
}

// =============================================================================
// Profile Button with notification badge
// =============================================================================

@Composable
private fun MobileBusinessProfileButton(
    avatarUrl: String?,
    notificationCount: Int,
    showDropdown: Boolean,
    onToggle: () -> Unit,
    onLoggedOut: () -> Unit
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var isLoggingOut by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Box(modifier = Modifier.position(Position.Relative)) {
        // Avatar
        Box(
            modifier = Modifier
                .size(44.px)
                .borderRadius(100.px)
                .overflow(Overflow.Hidden)
                .backgroundColor(AppColors.Text.Primary)
                .cursor(Cursor.Pointer)
                .onClick { onToggle() },
            contentAlignment = Alignment.Center
        ) {
            if (avatarUrl != null) {
                Div(attrs = {
                    ref { el ->
                        el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                            "onerror=\"this.style.display='none'\" " +
                            "style=\"width:100%;height:100%;object-fit:cover;\" />"
                        onDispose {}
                    }
                    style { property("width", "100%"); property("height", "100%") }
                })
            } else {
                Icon(src = AppIcons.Profile, modifier = Modifier.size(20.px))
            }
        }

        // Notification badge
        if (notificationCount > 0) {
            Box(
                modifier = Modifier
                    .size(12.px)
                    .borderRadius(100.px)
                    .backgroundColor(sportColors.primary)
                    .position(Position.Absolute)
                    .top(0.px)
                    .right(0.px)
                    .overflow(Overflow.Hidden),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    notificationCount.toString(),
                    modifier = Modifier
                        .fontSize(8.px)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier {
                            property("font-family", "'Sora', sans-serif")
                            property("line-height", "1")
                            property("transform", "translateY(0.5px)")
                        }
                )
            }
        }

        // Dropdown menu
        if (showDropdown) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(50.px)
                    .right(0.px)
                    .borderRadius(12.px)
                    .padding(6.px)
                    .zIndex(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, var(--l-s2, #0A0D14), var(--l-s2, #0A0D14)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("min-width", "140px")
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(2.px)) {
                    // Switch to Player
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .borderRadius(8.px)
                            .padding(10.px, 12.px)
                            .gap(8.px)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                scope.launch {
                                    container.userRepository?.updateActiveRole(
                                        UpdateActiveRoleRequest(role = "PLAYER")
                                    )?.onSuccess {
                                        Storage.save(StorageKeys.ACTIVE_ROLE, "PLAYER")
                                        ctx.router.navigateTo(Routes.ARENA)
                                    }
                                    onToggle()
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            t.headerSwitchToPlayer,
                            modifier = Modifier
                                .fontSize(14.px)
                                .fontWeight(500)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }

                    // Logout
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .borderRadius(8.px)
                            .padding(10.px, 12.px)
                            .gap(8.px)
                            .cursor(if (isLoggingOut) Cursor.NotAllowed else Cursor.Pointer)
                            .onClick {
                                if (!isLoggingOut) {
                                    isLoggingOut = true
                                    scope.launch {
                                        val logoutUseCase = container.getLogoutUseCase()
                                        logoutUseCase()
                                        container.clearAuthenticatedClients()
                                        OAuthHelper.resetGoogleSession()
                                        onToggle()
                                        onLoggedOut()
                                    }
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            if (isLoggingOut) t.headerLoggingOut else t.headerLogout,
                            modifier = Modifier
                                .fontSize(14.px)
                                .fontWeight(500)
                                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }
                }
            }
        }
    }
}

// =============================================================================
// Mobile Auth Buttons for Business pages
// =============================================================================

@Composable
private fun MobileBusinessGoogleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .height(36.px)
            .borderRadius(18.px)
            .padding(leftRight = 12.px)
            .gap(6.px)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.triggerGoogleSignIn(
                        onSuccess = { googleUser ->
                            val lang = OAuthHelper.localeToLanguageName(googleUser.locale)
                            loginViewModel.loginWithOAuth(
                                provider = "google",
                                providerId = googleUser.sub,
                                email = googleUser.email,
                                fullName = googleUser.name,
                                avatarUrl = googleUser.picture,
                                idToken = googleUser.idToken,
                                languages = lang?.let { listOf(it) },
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Google sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Google, modifier = Modifier.size(16.px))
        SpanText(
            if (isLoading) "..." else "Google",
            modifier = Modifier.fontSize(13.px).fontWeight(500).color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

@Composable
private fun MobileBusinessAppleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .height(36.px)
            .borderRadius(18.px)
            .padding(leftRight = 12.px)
            .gap(6.px)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.signInWithApple(
                        onSuccess = { email, name, providerId, idToken ->
                            loginViewModel.loginWithOAuth(
                                provider = "apple",
                                providerId = providerId,
                                email = email,
                                fullName = name,
                                idToken = idToken,
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = {
                            console.error("Apple sign-in error: $it")
                            isLoading = false
                        }
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Apple, modifier = Modifier.size(14.px))
        SpanText(
            if (isLoading) "..." else "Apple",
            modifier = Modifier.fontSize(13.px).fontWeight(500).color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}
